import React from 'react';
import { cn } from '@lqa-frontend/ui-system';

export type StatusBadgeStatus =
	'submitted' | 'approved' | 'in-progress' | 'denied';
export type StatusBadgeShape = 'rectangle' | 'pill';

const STATUS_STYLES: Record<StatusBadgeStatus, string> = {
	'submitted':
		'border-outline-interactive bg-interactive-light text-interactive',
	'approved': 'border-outline-success bg-success-light text-text-success',
	'in-progress': 'border-divider bg-base-2 text-neutral',
	'denied': 'border-outline-danger bg-danger-light text-text-danger',
};

const SHAPE_STYLES: Record<StatusBadgeShape, string> = {
	rectangle: 'rounded-s h-24 px-8 py-2 text-14 font-normal leading-large',
	pill: 'rounded-max px-8 py-2 text-12 font-semibold leading-lh6',
};

export interface IStatusBadgeProps {
	'status': StatusBadgeStatus;
	'label'?: string;
	'shape'?: StatusBadgeShape;
	'className'?: string;
	'data-testid'?: string;
}

const STATUS_LABELS: Record<StatusBadgeStatus, string> = {
	'submitted': 'Submitted',
	'approved': 'Approved',
	'in-progress': 'In Progress',
	'denied': 'Denied',
};

export function StatusBadge({
	status,
	label,
	shape = 'rectangle',
	className,
	...props
}: IStatusBadgeProps) {
	return (
		<span
			data-slot="status-badge"
			className={cn(
				'inline-flex items-center justify-center border',
				STATUS_STYLES[status],
				SHAPE_STYLES[shape],
				className
			)}
			{...props}
		>
			{label ?? STATUS_LABELS[status]}
		</span>
	);
}

/** Returns the Tailwind class string for a given status (for use in third-party components) */
export function getStatusBadgeStyles(status: StatusBadgeStatus): string {
	return STATUS_STYLES[status];
}

/** Status dot color for use in pickers/calendars */
export const STATUS_DOT_COLORS: Record<StatusBadgeStatus, string> = {
	'approved': 'var(--color-green-300)',
	'submitted': 'var(--color-brand-blue-300)',
	'in-progress': 'var(--color-zinc-300)',
	'denied': 'var(--color-red-200)',
};
